import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'common-ui/button';
import CustomDatePicker from 'common-ui/customDatePicker';
import Modal from 'common-ui/modal';
import Textarea from 'common-ui/textArea';
import { updateLead } from 'features/newsales/newsalesActions';

import { useLeadForm } from '../LeadFormContext';
import {
  BlueHeader,
  DateInputWrapper,
  DatesRow,
  ErrorMessage,
  FooterActions,
  FormBody,
  HeaderSubtitle,
  HeaderTitle,
  Label,
  ModalContentWrapper,
  StyledTextareaWrapper,
} from './CompetitorModal.styles';

const CompetitorModal = ({ isOpen, onClose, onNext, isEditMode = false, leadId = null }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formData, updateFormData, getLeadPayload, resetFormData } = useLeadForm();

  const [tariff, setTariff] = useState(formData.tariff || '');
  const [includedServices, setIncludedServices] = useState(formData.includedServices || '');
  const [contractEndDate, setContractEndDate] = useState(formData.contractEndDate || null);
  const [contactDate, setContactDate] = useState(formData.contactDate || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setTariff(formData.tariff || '');
      setIncludedServices(formData.includedServices || '');
      setContractEndDate(formData.contractEndDate || null);
      setContactDate(formData.contactDate || null);
    }
  }, [
    isOpen,
    formData.tariff,
    formData.includedServices,
    formData.contractEndDate,
    formData.contactDate,
  ]);

  useEffect(() => {
    if (isOpen) {
      setErrorMessage('');
    }
  }, [isOpen]);

  useEffect(() => {
    updateFormData({
      tariff,
      includedServices,
      contractEndDate,
      contactDate,
    });
  }, [tariff, includedServices, contractEndDate, contactDate]);

  const handleSave = async () => {
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const payload = getLeadPayload();
      const editPayload = { ...payload, businessType: formData.customerType };
      await dispatch(updateLead({ leadId, leadData: editPayload })).unwrap();
      onClose();
      navigate(`/lead-info?id=${leadId}`);
    } catch (error) {
      console.error('Error updating lead:', error);
      const errorMsg =
        error?.message ||
        error?.data?.message ||
        error?.error?.message ||
        (typeof error === 'string' ? error : null) ||
        'Failed to update lead. Please try again.';
      setErrorMessage(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    onNext?.();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} $noPadding={true} width="510px" footer={false}>
      <ModalContentWrapper>
        <BlueHeader>
          <HeaderTitle>Competitor</HeaderTitle>
          <HeaderSubtitle>All fields are optional</HeaderSubtitle>
        </BlueHeader>

        <FormBody>
          <Label>Tariff</Label>
          <StyledTextareaWrapper>
            <Textarea
              placeholder="Armenian or Latin letters"
              value={tariff}
              onChange={(e) => setTariff(e.target.value)}
              maxLength={250}
            />
          </StyledTextareaWrapper>
          <Label className="service">Included Services</Label>
          <StyledTextareaWrapper>
            <Textarea
              placeholder="Armenian or Latin letters"
              value={includedServices}
              onChange={(e) => setIncludedServices(e.target.value)}
              maxLength={250}
            />
          </StyledTextareaWrapper>
          <DatesRow>
            <DateInputWrapper>
              <CustomDatePicker
                label="Contract End Date"
                value={contractEndDate}
                onChange={setContractEndDate}
                placeholder="dd/mm/yy"
                height="48px"
              />
            </DateInputWrapper>
            <DateInputWrapper>
              <CustomDatePicker
                label="Contact Date"
                value={contactDate}
                onChange={setContactDate}
                placeholder="dd/mm/yy"
                height="48px"
              />
            </DateInputWrapper>
          </DatesRow>
        </FormBody>

        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

        <FooterActions>
          <Button
            className="modal-button-cancel"
            outlined
            onClick={onClose}
            disabled={isSubmitting}
          >
            Back
          </Button>
          {isEditMode ? (
            <Button
              className="modal-button-save"
              secondary
              onClick={handleSave}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          ) : (
            <Button
              className="modal-button-save"
              secondary
              onClick={handleNext}
              disabled={isSubmitting}
            >
              Next
            </Button>
          )}
        </FooterActions>
      </ModalContentWrapper>
    </Modal>
  );
};

export default CompetitorModal;
